import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, 
  faCheckCircle,
  faPhone,
  faBan,
  faUserSlash,
  faCalendarAlt,
  faExclamationTriangle,
  faTimes,
  faArrowRight,
  faCheck,
  faExternalLink
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';

const ProcessInterviewLines = () => {
  const [currentLine, setCurrentLine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    result_status: '',
    comment: '',
    reported_rendez_vous: ''
  });
  const [errors, setErrors] = useState({});
  const [showErrorModal, setShowErrorModal] = useState(false);
  const showErrorModalDismiss = useModalDismiss(() => setShowErrorModal(false), { backdropClose: false, enabled: showErrorModal });
  const [errorMessage, setErrorMessage] = useState('');
  const [limesurveyUrl, setLimesurveyUrl] = useState('');
  const [currentPost, setCurrentPost] = useState(null);

  useEffect(() => {
    fetchNextLine();
    fetchCurrentAssignment();
  }, []);

  const fetchCurrentAssignment = async () => {
    try {
      const response = await api.get('/cati/interviewer/dashboard');
      if (response.data.success && response.data.data.assignment?.currentPost) {
        setCurrentPost(response.data.data.assignment.currentPost);
      }
    } catch (error) {
      console.error('Error fetching current assignment:', error);
    }
  };

  const fetchNextLine = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cati/interviewer/next-line');
      
      console.log('Next line response:', response.data);
      
      if (response.data.success) {
        if (response.data.data) {
          setCurrentLine(response.data.data);
          if (response.data.limesurvey_url) {
            setLimesurveyUrl(response.data.limesurvey_url);
          }
          setFormData({
            result_status: '',
            comment: '',
            reported_rendez_vous: ''
          });
          setErrors({});
        } else {
          // No line found - show debug info if available
          if (response.data.debug) {
            console.log('Debug info:', response.data.debug);
            console.log('Interviewer ID:', response.data.debug.interviewer_id);
            console.log('Total lines:', response.data.debug.total_lines);
            console.log('Pending lines:', response.data.debug.pending_lines);
            console.log('Query SQL:', response.data.debug.query_sql);
            console.log('Query bindings:', response.data.debug.query_bindings);
          }
          setCurrentLine(null);
        }
      } else {
        console.error('Failed to fetch next line:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching next line:', error);
      console.error('Error response:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleStatusChange = (status) => {
    setFormData(prev => ({
      ...prev,
      result_status: status,
      comment: status !== 'hors_cible' ? '' : prev.comment,
      reported_rendez_vous: status !== 'rendez_vous' ? '' : prev.reported_rendez_vous
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.result_status) {
      newErrors.result_status = 'Veuillez sélectionner un statut';
    }

    if (formData.result_status === 'hors_cible' && !formData.comment.trim()) {
      newErrors.comment = 'Le commentaire est obligatoire pour "hors cible"';
    }

    if (formData.result_status === 'rendez_vous' && !formData.reported_rendez_vous) {
      newErrors.reported_rendez_vous = 'La date et heure de rendez-vous sont obligatoires';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getSurveyUrl = () => {
    if (!limesurveyUrl || !currentLine?.upload?.survey?.limesurvey_id) {
      return null;
    }

    const surveyId = currentLine.upload.survey.limesurvey_id;
    
    // Clean the base URL - remove trailing slash and any admin/remotecontrol paths
    let baseUrl = limesurveyUrl.replace(/\/$/, '');
    
    // Remove /admin/remotecontrol and anything after it, or /index.php/admin/remotecontrol
    baseUrl = baseUrl.replace(/\/admin\/remotecontrol.*$/i, '');
    baseUrl = baseUrl.replace(/\/index\.php\/admin\/remotecontrol.*$/i, '');
    
    // Extract just the domain and protocol (remove any existing paths)
    try {
      const urlObj = new URL(baseUrl);
      baseUrl = `${urlObj.protocol}//${urlObj.host}`;
    } catch (e) {
      // If URL parsing fails, try manual cleanup
      baseUrl = baseUrl.replace(/\/index\.php.*$/i, '');
    }
    
    // Construct the survey URL: {baseUrl}/index.php/{survey_id}?newtest=Y&lang=fr&Poste=...&TEL=...&Client=...&Code=...
    const lineData = currentLine.line_data || {};
    const params = new URLSearchParams();

    params.append('newtest', 'Y');
    params.append('lang', 'fr');

    // Poste = code poste of the interviewer (current post number)
    const poste = currentPost?.number != null ? String(currentPost.number) : '';
    if (poste) params.append('Poste', poste);

    // TEL = Num_tel from line
    const tel = lineData['Num_tel'] ?? lineData['num_tel'] ?? lineData['TEL'] ?? lineData['tel'] ?? lineData['Téléphone'] ?? lineData['téléphone'] ?? '';
    const telStr = Array.isArray(tel) ? tel.join('') : (tel != null ? String(tel).trim() : '');
    if (telStr) params.append('TEL', telStr);

    // Client = NOM CLIEN / NOM CLIENT from line (URL-encoded)
    const client = lineData['NOM CLIEN'] ?? lineData['NOM CLIENT'] ?? lineData['Nom client'] ?? lineData['nom client'] ?? '';
    const clientStr = Array.isArray(client) ? client.join('') : (client != null ? String(client).trim() : '');
    if (clientStr) params.append('Client', clientStr);

    // Code = code_agence of the line
    const code = currentLine.code_agence ?? '';
    if (code) params.append('Code', code);

    // Segments = from column "segment": mapped to survey codes (case-insensitive, multiple aliases)
    const segmentRaw = lineData['segment'] ?? lineData['Segment'] ?? '';
    const segmentVal = Array.isArray(segmentRaw) ? segmentRaw.join('') : (segmentRaw != null ? String(segmentRaw).trim() : '');
    const segmentMap = {
      'NOUVEAU CLIENT': '1', 'NV': '1', 'NOUVEAU CL': '1',
      'S1 - EXCEL': 'A7', 'S1': 'A7',
      'S2 - PREMIUM': 'A6', 'S2 - PREMI': 'A6', 'S2': 'A6',
      'S3 - POTENTIEL': 'A9', 'S3 - POTEN': 'A9', 'S3': 'A9',
      'S4 - AVENIR': 'A5', 'S4 - AVENI': 'A5', 'S4': 'A5',
      'S5 - UNIVERSEL': 'A13', 'S5 - UNIVE': 'A13', 'S5': 'A13',
      'S6 - LIB': 'A18', 'S6': 'A18',
      '02': 'A1', '03': 'A10', '01': 'A8', '04': 'A4', 'GP': 'A3', 'G1': 'A2',
      'CX': 'A12', 'G3': 'A11', '00': 'A14', 'G5': 'A16', 'G4': 'A15', 'G2': 'A17'
    };
    const segmentKey = Object.keys(segmentMap).find(k => k.toUpperCase() === segmentVal.toUpperCase());
    const segment = segmentKey ? segmentMap[segmentKey] : '';
    if (segment) params.append('Segments', segment);

    // Cible = from column "Marché": PARTICULIER → A1, PROFESSIONNELS → A2 (case-insensitive)
    const marcheRaw = lineData['Marché'] ?? lineData['Marche'] ?? lineData['marché'] ?? lineData['marche'] ?? '';
    const marcheVal = Array.isArray(marcheRaw) ? marcheRaw.join('') : (marcheRaw != null ? String(marcheRaw).trim() : '');
    const cibleMap = { 'PARTICULIER': 'A1', 'PROFESSIONNELS': 'A2' };
    const cibleKey = Object.keys(cibleMap).find(k => k.toUpperCase() === marcheVal.toUpperCase());
    const cible = cibleKey ? cibleMap[cibleKey] : '';
    if (cible) params.append('Cible', cible);

    const surveyUrl = `${baseUrl}/index.php/${surveyId}?${params.toString()}`;
    return surveyUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/cati/interviewer/lines/${currentLine.id}/complete`, {
        result_status: formData.result_status,
        comment: formData.comment || null,
        reported_rendez_vous: formData.reported_rendez_vous || null
      });

      if (response.data.success) {
        await fetchNextLine();
      }
    } catch (error) {
      console.error('Error completing line:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
        setShowErrorModal(true);
        // If upload is deactivated, fetch next line to refresh the view
        if (error.response.status === 403) {
          await fetchNextLine();
        }
      } else {
        setErrorMessage('Erreur lors de la soumission. Veuillez réessayer.');
        setShowErrorModal(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const statusOptions = [
    { value: 'ok', label: 'OK', icon: faCheckCircle, color: 'bg-green-500 hover:bg-green-600' },
    { value: 'boite_vocal', label: 'Boîte Vocale', icon: faPhone, color: 'bg-blue-500 hover:bg-blue-600' },
    { value: 'refus', label: 'Refus', icon: faBan, color: 'bg-red-500 hover:bg-red-600' },
    { value: 'hors_cible', label: 'Hors Cible', icon: faUserSlash, color: 'bg-orange-500 hover:bg-orange-600' },
    { value: 'rendez_vous', label: 'Rendez-vous', icon: faCalendarAlt, color: 'bg-purple-500 hover:bg-purple-600' },
    { value: 'injoignable', label: 'Injoignable', icon: faExclamationTriangle, color: 'bg-yellow-500 hover:bg-yellow-600' },
    { value: 'faux_numero', label: 'Faux Numéro', icon: faTimes, color: 'bg-gray-500 hover:bg-gray-600' }
  ];

  if (loading && !currentLine) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-600 animate-spin mb-4" />
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!currentLine) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon={faCheckCircle} className="text-6xl text-green-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Toutes les assignations sont complétées!</h2>
          <p className="text-gray-600">Vous avez terminé toutes les assignations.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Survey Info - always on top when we have a line */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">Titre de l'Enquête</p>
                <p className="text-lg text-gray-900">
                  {currentLine.upload?.survey?.title || '–'}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">ID LimeSurvey</p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-lg text-gray-900">
                    {currentLine.upload?.survey?.limesurvey_id || '–'}
                  </p>
                  {getSurveyUrl() && (
                    <a
                      href={getSurveyUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium w-full sm:w-auto flex-shrink-0"
                    >
                      <FontAwesomeIcon icon={faExternalLink} className="mr-2" />
                      Ouvrir l'enquête
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Client Info */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Informations Client</h2>
              <div className="space-y-3">
                {Object.entries(currentLine.line_data || {}).map(([key, value]) => (
                  <div key={key} className="border-b border-gray-200 pb-2">
                    <p className="text-sm font-medium text-gray-500 uppercase">{key}</p>
                    <p className="text-lg text-gray-900">{value || '-'}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Status Selection */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Statut de l'Appel</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Status Options */}
                <div>
                  <label className="block text-sm !font-bold text-gray-700 mb-3">Résultat *</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {statusOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleStatusChange(option.value)}
                        className={`flex items-center justify-center px-4 py-3 rounded-lg text-white transition-colors ${
                          formData.result_status === option.value 
                            ? option.color 
                            : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                      >
                        <FontAwesomeIcon icon={option.icon} className="mr-2" />
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {errors.result_status && (
                    <p className="mt-1 text-sm text-red-600">{errors.result_status}</p>
                  )}
                </div>

                {/* Comment (for hors_cible) */}
                {formData.result_status === 'hors_cible' && (
                  <div>
                    <label htmlFor="comment" className="block text-sm !font-bold text-gray-700 mb-2">
                      Commentaire *
                    </label>
                    <textarea
                      id="comment"
                      name="comment"
                      value={formData.comment}
                      onChange={handleInputChange}
                      rows={4}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                        errors.comment ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Expliquez pourquoi c'est hors cible..."
                    />
                    {errors.comment && (
                      <p className="mt-1 text-sm text-red-600">{errors.comment}</p>
                    )}
                  </div>
                )}

                {/* Rendez-vous (for rendez_vous) */}
                {formData.result_status === 'rendez_vous' && (
                  <div>
                    <label htmlFor="reported_rendez_vous" className="block text-sm !font-bold text-gray-700 mb-2">
                      Date et Heure du Rendez-vous *
                    </label>
                    <input
                      type="datetime-local"
                      id="reported_rendez_vous"
                      name="reported_rendez_vous"
                      value={formData.reported_rendez_vous}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                        errors.reported_rendez_vous ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.reported_rendez_vous && (
                      <p className="mt-1 text-sm text-red-600">{errors.reported_rendez_vous}</p>
                    )}
                  </div>
                )}

                {/* Comment (optional for other statuses) */}
                {formData.result_status && formData.result_status !== 'hors_cible' && (
                  <div>
                    <label htmlFor="comment" className="block text-sm !font-bold text-gray-700 mb-2">
                      Commentaire (optionnel)
                    </label>
                    <textarea
                      id="comment"
                      name="comment"
                      value={formData.comment}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Ajoutez un commentaire si nécessaire..."
                    />
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      Suivant
                      <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Erreur
                  </h3>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowErrorModal(false);
                  setErrorMessage('');
                  window.location.reload();
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-700 mb-6">
                {errorMessage}
              </p>

              <div className="flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowErrorModal(false);
                    setErrorMessage('');
                    window.location.reload();
                  }}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessInterviewLines;

